import 'project/polyfill'
import React from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { Query } from '@prismicio/types'

import {
  TAddon,
  TFAQList,
  TReviews,
  TPlanSummary,
  TBlogPostResult,
} from 'common/cms-types'
import {
  heroGapMd,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { Utils } from 'common/utils'
import { getPlans } from 'common/planData'
import Supademo from 'components/Supademo'
import { PlanSlug } from 'common/planSlugs'
import { getFAQList } from 'common/cms-sdk'
import Button from 'components/forms/Button'
import FAQList from 'components/util/FAQList'
import { createClient } from '../../prismicio'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import HeroBanner from 'components/hero/HeroBanner'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { ButtonType } from '../../types/global'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import PageContainer from 'components/util/containers/PageContainer'
import ForAuEnterprise from 'components/cards/new/au/ForAuEnterprise'
import { auHomeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import SalesPitchCard from 'components/cards/new/common/SalesPitchCard'
import EmbededKonnectForm from 'components/sections/EmbededKonnectForm'
import YourMailAssistant from 'components/cards/new/au/YourMailAssistant'
import EnterpriseBenefits from 'components/sections/aus/EnterpriseBenefits'
import TrustedPartnerCard from 'components/cards/new/au/TrustedPartnerCard'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import SeamlessIntegration from 'components/cards/new/au/SeamlessIntegration'
import DesigendForKeySectors from 'components/sections/aus/DesigendForKeySectors'
import { MailCountTrust } from 'components/MailCountTrust'
import { getMailCountRounded } from 'common/utils/getMailCountString'
import WhyAusBusinessChooseUs from 'components/cards/new/au/WhyAusBusinessChooseUs'
import EnterpriseLevelSupport from 'components/cards/new/au/EnterpriseLevelSupport'
import SecureCompliantAndTransparent from 'components/cards/new/au/SecureCompliantTransparent'
import { Tenant } from 'common/tenant'

export type EnterprisePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  reviews: TReviews
  addons: TAddon[]
  faqList: TFAQList
}

const EnterprisePage: NextPageWithLayout<EnterprisePageType> = ({
  faqList,
  plans,
}) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Provide your clients with a professional Sydney address, full mail digitisation, and regulatory compliance. Trusted by top banks, accountants, and advisers.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/au/enterprise',
    },
    title:
      'Registered Office Solutions for Australian Banks, Accountants & Agents',
  }
  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
  const size = useWindowSize()
  return (
    <SeoContainer
      alternate={[
        { href: '/enterprise', lang: 'en-gb' },
        { href: '/au/enterprise', lang: 'en-au' },
      ]}
      breadcrumb={auHomeBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column ', heroGapMd)}>
          <HeroBanner
            logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
            hideDownloadButtons
            hideFooter
            robotImage={{
              alt: 'HaiRo™ robot mascot holding tie',
              dimensions: {
                height: 957,
                width: 702,
              },
              src: '/images/new/au/robot-enterprise.png',
            }}
            title={
              <h1 className='fs-fluid-34-64 lh-105 lh-lg-108'>
                Help Your Clients Establish a Registered Office in Australia
              </h1>
            }
            subtitle={'With mail handling and digital document management'}
            subtitleContainerClassName='fs-fluid-24-26 fw-semibold mb-5 d-block lh-114 mt-4'
            cta={
              <SubscriptionPricing
                buttonType={ButtonType.AU_ENTERPRISE_PAGE_HERO}
                hideRegister
                hidePricing
                tenant={Tenant.AU}
                showLink
                planSummary={planSummary}
              />
            }
            description={
              <div className={classNames('mb-2')}>
                <div className='fs-fluid-18-21 fw-semibold lh-123'>
                  Give your clients a compliant registered office solution based
                  in Sydney with automated mail handling, document digitisation,
                  and full API or portal access. Our flexible offering is
                  designed for accountants, fintechs, formation services, and
                  enterprise platforms looking to scale without operational
                  friction.
                </div>
              </div>
            }
          />
          <div className={classNames('container d-flex flex-column')}>
            <ForAuEnterprise />
          </div>
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div className={`d-none d-lg-flex flex-column ${sectionGapSmall}`}>
            <div className={classNames('d-flex flex-column', sectionGapSmall)}>
              <div className={classNames(sectionPadding, 'd-flex flex-column')}>
                <h2
                  id='demo'
                  className={classNames(
                    'fs-fluid-36-80 lh-105 fw-semibold text-center',
                  )}
                >
                  Our <span className='color-pink2'>Partner Portal</span>.
                </h2>
                <h3
                  style={{
                    maxWidth: 682,
                  }}
                  className='fw-semibold text-muted mt-lg-3 mt-2 text-center fs-fluid-20-36 mx-auto'
                >
                  With the Partner Portal, you can manage your clients
                  seamlessly and efficiently.
                </h3>
              </div>
              <div>
                <Supademo showcaseId={'cmbs3di7n00vy090ismxakcn0'} />
              </div>
            </div>
          </div>
          <EnterpriseBenefits />
          <SeamlessIntegration />
          <DesigendForKeySectors />
          <TrustedPartnerCard />
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              <MailCountTrust highlightClassName='color-pink2' />
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial
                textSize='large'
                annaReview='"the ability to offer address services under our brand made a big impact. It’s a high-value, low-effort addition to our setup process."'
                tideReview='“adding a virtual address option helped our clients register quicker and protect their privacy. We’ve seen strong uptake and retention." '
              />
              <div className='d-flex justify-content-center align-items-center mt-lg-16'>
                <Button className='w-100 w-lg-auto'>Book a demo</Button>
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'container d-flex flex-column',
            sectionGapSmall,
          )}
        >
          <WhyAusBusinessChooseUs />
          <YourMailAssistant />
          <SecureCompliantAndTransparent />
          <EnterpriseLevelSupport />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          {size.isLgOrHigher && (
            <div>
              <h1 className='fw-semibold text-center'>
                Take the <span className='color-pink2'>First Step</span>
              </h1>
              <SalesPitchCard
                pitchText={
                  <h2 className='fs-48 lh-111'>
                    <span className='color-pink2'>We've processed over</span>{' '}
                    {getMailCountRounded()} mail items{' '}
                    <span className='color-pink2'>and supported</span> 100,000+
                    customers to date.{' '}
                    <span className='color-pink2'>Now we're doing it for</span>{' '}
                    Australian partners{' '}
                    <span className='color-pink2'>too.</span>
                  </h2>
                }
                cta={
                  <Link
                    className='align-self-lg-start mt-10'
                    href={'/au/enterprise#konnect-form'}
                  >
                    <Button>Book a Demo</Button>
                  </Link>
                }
                desktopImage='/images/new/au/vo-robot-pointing-right.png'
              />
            </div>
          )}
          <FAQList
            title={faqList.data.title}
            subtitle={faqList.data.subtitle}
            slices={faqList.data.slices}
            expandAllButton
            expandAllButtonDesktopOnly
          />
          <EmbededKonnectForm tenant={Tenant.AU} />
        </div>
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

EnterprisePage.getLayout = (page: React.ReactNode) => (
  <PageContainer tenant={Tenant.AU}>{page}</PageContainer>
)
export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans(undefined, String(Tenant.AU))
    const faqList = await getFAQList(client, lang, 'au-enterprise')
    return {
      props: {
        faqList,
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default EnterprisePage
